import { apiService } from "api/apiService";
import { toAxiosUploadProgress } from "shared/utils/axiosProgress";
import useHelpers from "composables/useHelpers.js";
import { cloneDeep } from "lodash";
import { storeToRefs } from "pinia";
import { useLearningObjectivesStore } from "store/modules/learningObjectivesStore";

export const DEFAULT_LIBRARY_CLUSTER = Object.freeze({
  id: null,
  type: "library_cluster",
  school_id: null,
  name: "",
  description: "",
  shortage: "",
  subjects: [],
  education_year_levels: [],
  // Colleagues may edit a new library cluster by default (#1175).
  editable: true,
  attachments: [],
  learning_objectives: [],
  library_learning_objective_ids: [],
  links: [],
  user: {},
  videos: [],
  trashed: false,
  meta: {
    can_update: true,
    can_delete: true,
  },
});

export class LibraryCluster {
  constructor(data = {}) {
    this.id = data.id ?? DEFAULT_LIBRARY_CLUSTER.id;
    this.type = data.type ?? DEFAULT_LIBRARY_CLUSTER.type;
    this.school_id = data.school_id ?? DEFAULT_LIBRARY_CLUSTER.school_id;
    this.name = data.name ?? DEFAULT_LIBRARY_CLUSTER.name;
    this.description = data.description ?? DEFAULT_LIBRARY_CLUSTER.description;
    this.shortage = data.shortage ?? DEFAULT_LIBRARY_CLUSTER.shortage;
    this.subjects = cloneDeep(
      data.subjects ?? DEFAULT_LIBRARY_CLUSTER.subjects,
    );
    this.education_year_levels = cloneDeep(
      data.education_year_levels ??
        DEFAULT_LIBRARY_CLUSTER.education_year_levels,
    );
    this.editable = data.editable ?? DEFAULT_LIBRARY_CLUSTER.editable;
    this.attachments = cloneDeep(
      data.attachments ?? DEFAULT_LIBRARY_CLUSTER.attachments,
    );
    this.learning_objectives = cloneDeep(
      data.learning_objectives ?? DEFAULT_LIBRARY_CLUSTER.learning_objectives,
    );
    this.library_learning_objective_ids = cloneDeep(
      data.library_learning_objective_ids ??
        DEFAULT_LIBRARY_CLUSTER.library_learning_objective_ids,
    );
    this.links = cloneDeep(data.links ?? DEFAULT_LIBRARY_CLUSTER.links);
    this.user = cloneDeep(data.user ?? DEFAULT_LIBRARY_CLUSTER.user);
    this.videos = cloneDeep(data.videos ?? DEFAULT_LIBRARY_CLUSTER.videos);
    this.trashed = data.trashed ?? DEFAULT_LIBRARY_CLUSTER.trashed;
    this.meta = cloneDeep(data.meta ?? DEFAULT_LIBRARY_CLUSTER.meta);
  }

  hasChanges() {
    return (
      this.name !== DEFAULT_LIBRARY_CLUSTER.name ||
      this.description !== DEFAULT_LIBRARY_CLUSTER.description ||
      this.shortage !== DEFAULT_LIBRARY_CLUSTER.shortage ||
      this.subjects.length !== DEFAULT_LIBRARY_CLUSTER.subjects.length ||
      this.education_year_levels.length !==
        DEFAULT_LIBRARY_CLUSTER.education_year_levels.length ||
      this.attachments.length !== DEFAULT_LIBRARY_CLUSTER.attachments.length ||
      this.learning_objectives.length !==
        DEFAULT_LIBRARY_CLUSTER.learning_objectives.length ||
      this.links.length !== DEFAULT_LIBRARY_CLUSTER.links.length ||
      this.videos.length !== DEFAULT_LIBRARY_CLUSTER.videos.length
    );
  }

  // Stage the added learning objectives locally only (deduped). The connection
  // is not persisted here — it is synced to the backend when the cluster form
  // is saved (see syncLearningObjectives), so an unsaved add is discarded on
  // reload.
  async addLearningObjectivesToModel(addedLearningObjectives) {
    this.learning_objectives.push(
      ...addedLearningObjectives.filter(
        (learningObjective) =>
          !this.learning_objectives.some(
            (existingLearningObjective) =>
              existingLearningObjective.id === learningObjective.id,
          ),
      ),
    );
  }

  // Persist the cluster's full learning-objective set. The endpoint is a sync:
  // present ids are (re)attached in list order, omitted ids are detached — so a
  // single call covers staged adds, removes and reordering.
  async syncLearningObjectives() {
    const { schoolId } = useHelpers();

    const { data } = await apiService.post(
      `/api/school/${schoolId}/library-clusters/${this.id}/add-library-learning-objectives`,
      {
        library_learning_objective_ids: this.learning_objectives.map(
          ({ library_learning_objective_id = null, id = null }) =>
            library_learning_objective_id ?? id,
        ),
      },
    );

    this.learning_objectives = data;
    this.library_learning_objective_ids = data.map(
      ({ library_learning_objective_id = null, id = null }) =>
        library_learning_objective_id ?? id,
    );
  }

  async store({ onUploadProgress } = {}) {
    // No model set so we can't get it from the model.
    const { schoolId } = useHelpers();

    const learningObjectivesStore = useLearningObjectivesStore();
    const { libraryClusters } = storeToRefs(learningObjectivesStore);

    const { rewriteModelToFormData } = useHelpers();

    const formData = rewriteModelToFormData(this);

    const {
      data: { data: storedCluster },
    } = await apiService.post(
      `/api/school/${schoolId}/library-clusters`,
      formData,
      { onUploadProgress: toAxiosUploadProgress(onUploadProgress) },
    );

    // Keep the locally staged learning objectives; the create response carries
    // none, so assign everything except that field before syncing them.
    // eslint-disable-next-line no-unused-vars
    const { learning_objectives, ...clusterData } = storedCluster;
    Object.assign(this, clusterData);

    this.id = storedCluster.id;

    if (this.learning_objectives.length) {
      await this.syncLearningObjectives();
    }

    libraryClusters.value.push(this);

    return this;
  }

  objectToUpdate() {
    // These keys do not have to be send to the backend to update this library cluster
    const hiddenKeys = [
      "library_learning_objective_ids",
      "learning_objectives",
      "meta",
      "trashed",
    ];

    return Object.fromEntries(
      Object.entries(this).filter(([key]) => !hiddenKeys.includes(key)),
    );
  }

  async update({ onUploadProgress } = {}) {
    const learningObjectivesStore = useLearningObjectivesStore();
    const { updateLibraryCluster } = learningObjectivesStore;

    // The update response carries the previously-persisted objectives; keep the
    // locally staged set so the sync below reflects the user's pending changes.
    const stagedLearningObjectives = [...this.learning_objectives];

    const dataToUpdate = this.objectToUpdate();
    const updatedCluster = await updateLibraryCluster(dataToUpdate, {
      onUploadProgress,
    });

    Object.assign(this, updatedCluster);
    this.learning_objectives = stagedLearningObjectives;

    await this.syncLearningObjectives();

    return this;
  }

  // Unstage the learning objective locally only. The detach is persisted when
  // the cluster form is saved (syncLearningObjectives omits it), so an unsaved
  // removal is undone on reload.
  removeLearningObjectiveFromCluster(learningObjective) {
    this.learning_objectives = this.learning_objectives.filter(
      (lo) => lo.id !== learningObjective.id,
    );

    this.library_learning_objective_ids = this.learning_objectives.map(
      ({ library_learning_objective_id = null, id = null }) =>
        library_learning_objective_id ?? id,
    );
  }

  async upsert({ onUploadProgress } = {}) {
    // Early return if there are no changes
    if (!this.hasChanges()) {
      return;
    }

    return this.id === null
      ? await this.store({ onUploadProgress })
      : await this.update({ onUploadProgress });
  }
}
